import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Icon } from 'Components/atoms/Icon';
import { Image } from 'Components/atoms/Image';
import { Price } from 'Components/atoms/Price';
import { Link } from 'Components/molecules/Link';
import { withPolaris } from 'Components/protons/Polaris';
import { asyncTrackLinkClick } from 'SharedHelpers/tracking';

import './_style.scss';

/**
 * NewCarDealsCard component renders a card with product information including image, title, prices, and links.
 */
const NewCarDealsCard = ({
  cashPrice: {
    min: cashPrice,
    prefixText: cashPrefixText,
  },
  context: { cssPrefix },
  extraClassNames,
  icon,
  image: {
    alt,
    height,
    src,
    width,
  },
  marketplaceLink: {
    label: marketplaceLabel,
    tracking: marketplaceTracking,
    url: marketplaceUrl,
    extraLabel: marketplaceLinkExtraLabel,
  },
  newMarketplaceLink: {
    label: newMarketplaceLabel,
    tracking: newMarketplaceTracking,
    url: newMarketplaceUrl,
  },
  savingPrice: {
    min: savingMin,
    prefixText: savingPrefixText,
  },
  title,
}) => {
  /**
   * Handles the marketplace link click event.
   * Pushes the tracking object to the dataLayer.
   * @example of tracking object:
   * {
   *  event: 'marketplace.button_click',
   *  'event.type': 'GYCCustom',
   *  'marketplace_button.placement': 'GYCHotDealsBlockCustom',
   *  }
   */
  const handleLinkClick = async (trackingPayload) => {
    if (typeof window !== 'undefined' && trackingPayload && Object.keys(trackingPayload).length > 0) {
      window.dataLayer = window.dataLayer || [];
      await asyncTrackLinkClick(trackingPayload);
    }
  };

  const renderDealsLink = ({
    url, trackingObj, children,
  }) => (
    url ? (
      <Link
        extraClassNames={{
          [`${cssPrefix}__new-car-deals-card--link`]: true,
        }}
        href={url}
        loadStyles={false}
        onClick={async () => {
          if (trackingObj) {
            await handleLinkClick(trackingObj);
          }
        }}
      >
        {children}
        {icon && (
        <div className={`${cssPrefix}__new-car-deals-card--link-icon`}>
          <Icon icon={icon} />
        </div>
        )}
      </Link>
    ) : null
  );

  return (
    <div
      className={classNames({
        [`${cssPrefix}__new-car-deals-card`]: true,
        ...extraClassNames,
      })}
    >
      <div className={`${cssPrefix}__new-car-deals-card--media`}>
        {src && ( // Only render the image if the src is provided, but keep the container for layout purposes
          <Image
            extraClassNames={{
              [`${cssPrefix}__new-car-deals-card--image`]: true,
            }}
            disableTransforms
            src={src}
            alt={alt}
            height={height > 0 ? height : undefined}
            width={width > 0 ? width : undefined}
          />
        )}
      </div>
      <div className={`${cssPrefix}__new-car-deals-card--content`}>
        <p className={`${cssPrefix}__new-car-deals-card--title`}>{title}</p>
        <div className={`${cssPrefix}__new-car-deals-card--ctas`}>
          {newMarketplaceUrl && (
            renderDealsLink({
              url: newMarketplaceUrl,
              trackingObj: newMarketplaceTracking,
              children: (
                <div className={`${cssPrefix}__new-car-deals-card--link-label`}>
                  {newMarketplaceLabel}
                  {cashPrice > 0 && (
                    <Price
                      extraClassNames={{
                        [`${cssPrefix}__new-car-deals-card--cash-price`]: true,
                      }}
                      minOnly
                      min={cashPrice}
                      prefixText={cashPrefixText}
                    />
                  )}
                  {savingMin > 0 && (
                    <Price
                      extraClassNames={{
                        [`${cssPrefix}__new-car-deals-card--saving`]: true,
                      }}
                      minOnly
                      min={savingMin}
                      prefixText={savingPrefixText}
                    />
                  )}
                </div>
              ),
            })
          )}
          {marketplaceUrl && (
            renderDealsLink({
              url: marketplaceUrl,
              trackingObj: marketplaceTracking,
              children: (
                <div className={`${cssPrefix}__new-car-deals-card--link-label`}>
                  {marketplaceLabel}
                  {marketplaceLinkExtraLabel && (
                    <p className={`${cssPrefix}__new-car-deals-card--extra-label`}>{marketplaceLinkExtraLabel}</p>
                  )}
                </div>
              ),
            })
          )}
        </div>
      </div>
    </div>
  );
};

NewCarDealsCard.propTypes = {
  cashPrice: PropTypes.shape({
    min: PropTypes.number,
    prefixText: PropTypes.string,
  }),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape({}),
  icon: PropTypes.string,
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.number,
    src: PropTypes.string,
    width: PropTypes.number,
  }),
  marketplaceLink: PropTypes.shape({
    extraLabel: PropTypes.string,
    label: PropTypes.string,
    tracking: PropTypes.shape(),
    url: PropTypes.string,
  }),
  newMarketplaceLink: PropTypes.shape({
    label: PropTypes.string,
    tracking: PropTypes.shape(),
    url: PropTypes.string,
  }),
  savingPrice: PropTypes.shape({
    min: PropTypes.number,
    postText: PropTypes.string,
    prefixText: PropTypes.string,
  }),
  title: PropTypes.string,
};

NewCarDealsCard.defaultProps = {
  cashPrice: {
    min: 0,
    prefixText: '',
  },
  context: { cssPrefix: 'polaris' },
  extraClassNames: {},
  icon: null,
  image: {
    alt: '',
    height: 0,
    src: '',
    width: 0,
  },
  marketplaceLink: {
    extraLabel: '',
    label: '',
    tracking: {},
    url: '',
  },
  newMarketplaceLink: {
    label: '',
    tracking: {},
    url: '',
  },
  savingPrice: {
    min: 0,
    postText: '',
    prefixText: '',
  },
  title: '',
};

export default withPolaris(NewCarDealsCard);
